import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertCircle } from 'lucide-react';
import { normalizeURL, isValidURL } from '../utils/urlUtils';

const URLForm = ({ onAnalysisStart }) => {
  const [url, setUrl] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const validateURL = (input) => {
    return isValidURL(input);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setIsValid(false);
      setErrorMessage('Please enter a URL');
      return;
    }

    if (!validateURL(url)) {
      setIsValid(false);
      setErrorMessage('Please enter a valid URL (e.g., example.com or www.example.com)');
      return;
    }

    setIsValid(true);
    setErrorMessage('');
    setIsSubmitting(true);

    try {
      // Normalize URL before sending
      const normalizedUrl = normalizeURL(url);
      
      // Navigate to analysis page with normalized URL
      navigate('/analysis', { state: { url: normalizedUrl } });
      
      if (onAnalysisStart) {
        onAnalysisStart();
      }
    } catch (error) {
      console.error('Navigation error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setIsValid(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUrl(value);
    
    if (value.trim() && !isValid) {
      setIsValid(true);
      setErrorMessage('');
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <div className="flex items-center">
            <div className="relative flex-1">
              <input
                type="text"
                value={url}
                onChange={handleInputChange}
                placeholder="Enter your website URL (e.g., example.com or www.example.com)"
                className={`w-full px-4 py-4 pr-12 text-lg border-2 rounded-lg focus:outline-none focus:ring-4 transition-all duration-200 text-gray-900 placeholder-gray-500 bg-white ${
                  isValid
                    ? 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
                    : 'border-red-500 focus:border-red-500 focus:ring-red-100'
                }`}
                style={{ color: '#111827' }}
                disabled={isSubmitting}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          
          {!isValid && errorMessage && (
            <div className="mt-2 flex items-center text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errorMessage}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !url.trim()}
          className={`w-full py-4 px-8 text-lg font-semibold text-white rounded-lg transition-all duration-200 ${
            isSubmitting || !url.trim()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5'
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Starting Enhanced AI Analysis...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <Search className="w-5 h-5 mr-2" />
              Analyze with Enhanced AI
            </div>
          )}
        </button>
      </form>

      <div className="mt-4 text-center text-sm text-blue-100">
        <p>✓ Enhanced AI-powered analysis • ✓ Free • ✓ No registration • ✓ Instant results</p>
      </div>
    </div>
  );
};

export default URLForm;
